import { valueOrNull } from '@whitelabel/helpers/utils';
import { getSessionStorage } from '@whitelabel/helpers/storageUtils';
import { CLAIM_JOURNEY_DATA, CLAIM_JOURNEY_ID, CONTACT_JOURNEY_ID } from './constants';
import { dateISOtoFullDateAndTime } from './date';
import {
  IBWClaimsJourneyTrackingArgs,
  IBWStoredClaimSelectorDataArgs,
  IBWClaimDetailsCommunicationsTrackingArgs,
  IBWRentalCoverBannerImpressionTrackingArgs,
  IBWRentalCoverBannerClickTrackingArgs,
  IBWCallUsClickTrackingArgs,
  IBWCESScoreTrackingArgs,
  IBWContactModalTrackingArgs,
  IBwAtpQuoteSuccessArgs,
  IBwAtpEventArgs,
  IBwAtpErrorEventArgs,
  IBwAtpPurchaseEventArgs,
  IBwAtpModalActionEventArgs,
  IBWFNOLSectionTimeTrackingArgs,
  IBWFNOLSubmittedPageTimeTrackingArgs,
  IBWQuickActionsTrackingArgs,
  IBWSelfServiceTrackingArgs,
  IBWOCREventArgs,
  IBWFileUploadEventArgs,
  IBWClaimStartedEventArgs,
  IBWClaimSubmittedEventArgs,
  IBWPayoutReceiptClickTrackingArgs,
} from './types';

export const getClaimJourneyID = (bookingID?: string): string | null => {
  if (!bookingID) return null;
  return localStorage.getItem(`${CLAIM_JOURNEY_ID}_${bookingID}`);
};

/** Returns the existing journey ID or creates and persists a new one. */
export const getOrCreateClaimJourneyID = (bookingID: string): string => {
  const existing = getClaimJourneyID(bookingID);
  if (existing) return existing;
  const newJourneyId = `${bookingID}-${dateISOtoFullDateAndTime(new Date().toISOString())}`;
  localStorage.setItem(`${CLAIM_JOURNEY_ID}_${bookingID}`, newJourneyId);
  return newJourneyId;
};

export const bwClaimsJourneyTracking = ({
  claimJourneyID,
  bookingID,
  claimReference,
  partnerID,
  partnerName,
  subsidiaryID,
  subsidiaryName,
  policyTypeGroupName,
  policyName,
  claimSelectorName,
  decisionPointsName,
  decisionPointsAnswer,
  claimTypeName,
  sectionName,
  hasLeavedClaimJourney,
  completed,
  submitted,
  xclaimValidationError,
  fieldValidationErrorCount,
  hasClickedHelpCenter,
  hasClickedClaimSubmittedAlertLink,
  hasClickedClaimSubmittedCopyURL,
  hasClickedClaimSubmittedViewClaimButton,
  documentName,
  helpArticleSlug,
  fileUploadErrors,
  entryPoint,
  variant,
  isFirstSection,
}: IBWClaimsJourneyTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  if (!claimJourneyID && bookingID) {
    getOrCreateClaimJourneyID(bookingID);
  }

  const storedClaimSelectorData: IBWStoredClaimSelectorDataArgs =
    JSON.parse(localStorage.getItem(CLAIM_JOURNEY_DATA) as string) ?? {};
  const {
    policyName: storedPolicyName,
    claimSelectorName: storedClaimSelectorName,
    decisionPointsName: storedDecisionPointName,
    decisionPointsAnswer: storedDecisionPointsAnswer,
  } = storedClaimSelectorData;

  if (claimSelectorName && (policyName || (decisionPointsName && decisionPointsAnswer))) {
    localStorage.setItem(
      CLAIM_JOURNEY_DATA,
      JSON.stringify({
        claimSelectorName,
        policyName: policyName ?? valueOrNull(storedPolicyName),
        decisionPointsName,
        decisionPointsAnswer,
      }),
    );
  }

  window.bwtag('record', 'claims_journey', {
    claimJourneyID: claimJourneyID || getClaimJourneyID(bookingID) || '',
    policyName: policyName ?? valueOrNull(storedPolicyName),
    claimSelectorName: claimSelectorName ?? valueOrNull(storedClaimSelectorName),
    decisionPointsName: decisionPointsName ?? valueOrNull(storedDecisionPointName),
    decisionPointsAnswer: decisionPointsAnswer ?? valueOrNull(storedDecisionPointsAnswer),
    claimTypeName: valueOrNull(claimTypeName),
    sectionName: valueOrNull(sectionName),
    hasLeavedClaimJourney: hasLeavedClaimJourney ?? false,
    completed: completed ?? false,
    submitted: submitted ?? false,
    hasClickedHelpCenter: hasClickedHelpCenter ?? false,
    hasClickedClaimSubmittedAlertLink: hasClickedClaimSubmittedAlertLink ?? false,
    hasClickedClaimSubmittedViewClaimButton: hasClickedClaimSubmittedViewClaimButton ?? false,
    hasClickedClaimSubmittedCopyURL: hasClickedClaimSubmittedCopyURL ?? false,
    xclaimValidationError: valueOrNull(xclaimValidationError),
    fieldValidationErrorCount,
    bookingID,
    ins: bookingID,
    claim_cla: claimReference,
    claimReference,
    documentName,
    helpArticleSlug,
    fileUploadErrors,
    partner: partnerName,
    partnerID,
    partnerName,
    subsidiaryID,
    subsidiaryName,
    policyTypeGroupName,
    entryPoint,
    variant,
    isFirstSection,
  });
};

export const bwClaimDetailsCommunicationsTracking = ({
  link,
  bookingID,
  claimReference,
  status,
  dateSent,
  linkText,
  emailTemplateId,
  emailTemplateSlug,
  partnerID,
  partnerName,
  subsidiaryID,
  subsidiaryName,
  policyTypeGroupName,
}: IBWClaimDetailsCommunicationsTrackingArgs): void => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'claim_communications', {
    link,
    bookingID,
    ins: bookingID,
    claimReference,
    claim_cla: claimReference,
    status,
    dateSent,
    linkText,
    emailTemplateId,
    emailTemplateSlug,
    partnerID,
    partnerName,
    subsidiaryID,
    subsidiaryName,
    policyTypeGroupName,
  });
};

export const bwPASHTracking = (anchorID: string, partnerName: string, languageCode: string) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'click', {
    type: 'anchor_id',
    value: anchorID,
    partner_name: partnerName,
    language_code: languageCode,
  });
};

export const bwRentalCoverBannerImpressionTracking = ({
  domID,
  source,
  CountryOfTravelCode,
  CustomerCountryCode,
  FromDate,
  ToDate,
  PolicyType,
}: IBWRentalCoverBannerImpressionTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'impression', {
    domID,
    source,
    CountryOfTravelCode,
    CustomerCountryCode,
    FromDate,
    ToDate,
    PolicyType,
  });
};

export interface IBWImpressionTrackingArgs {
  analyticsID: string;
  [key: string]: string | string[] | undefined;
}

export const bwImpressionTracking = ({ analyticsID, ...props }: IBWImpressionTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'impression', {
    analyticsID,
    ...props,
  });
};

export const bwRentalCoverBannerClickTracking = ({
  bannerID,
  domID,
  CountryOfTravelCode,
  CustomerCountryCode,
  FromDate,
  ToDate,
  PolicyType,
  source,
}: IBWRentalCoverBannerClickTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'click', {
    bannerID,
    domID,
    CountryOfTravelCode,
    CustomerCountryCode,
    FromDate,
    ToDate,
    PolicyType,
    source,
  });
};

export const bwAtpQuoteSuccessTracking = ({
  policyholderCountry,
  partnerID,
  currency,
  totalPrice,
}: IBwAtpQuoteSuccessArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'atp-banner-quote-success', {
    policyholderCountry,
    partnerID,
    currency,
    totalPrice,
  });
};

const recordAtpEvent = (eventName: string, data: Record<string, unknown>) => {
  if (window.isHeadless || !window.bwtag) return;
  try {
    window.bwtag('record', eventName, data);
  } catch (e) {
    // Analytics must never break the product flow
  }
};

export const bwAtpQuoteErrorTracking = (args: IBwAtpErrorEventArgs) => recordAtpEvent('atp-quote-error', { ...args });

export const bwAtpOfferImpressionTracking = (args: IBwAtpEventArgs) =>
  recordAtpEvent('atp-offer-impression', { ...args });

export const bwAtpBannerCtaClickedTracking = (args: IBwAtpEventArgs) =>
  recordAtpEvent('atp-banner-cta-clicked', { ...args });

export const bwAtpAddTravelersClickedTracking = (args: IBwAtpEventArgs) =>
  recordAtpEvent('atp-add-travelers-clicked', { ...args });

export const bwAtpAddTravelersQuoteModifiedTracking = (args: IBwAtpEventArgs) =>
  recordAtpEvent('atp-add-travelers-quote-modified', { ...args });

export const bwAtpOfferAcceptedTracking = (args: IBwAtpEventArgs) => recordAtpEvent('atp-offer-accepted', { ...args });

export const bwAtpOfferRejectedTracking = (args: IBwAtpEventArgs) => recordAtpEvent('atp-offer-rejected', { ...args });

export const bwAtpPaymentInitiatedTracking = (args: IBwAtpEventArgs) =>
  recordAtpEvent('atp-payment-initiated', { ...args });

export const bwAtpPaymentErrorTracking = (args: IBwAtpErrorEventArgs) =>
  recordAtpEvent('atp-payment-error', { ...args });

export const bwAtpPurchaseTracking = (args: IBwAtpPurchaseEventArgs) => recordAtpEvent('atp-purchase', { ...args });

export const bwAtpModalActionTracking = (args: IBwAtpModalActionEventArgs) =>
  recordAtpEvent('atp-modal-action', { ...args });

export const bwCallUsClickTracking = ({ partnerSlug }: IBWCallUsClickTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'click', {
    analyticsID: 'call-us-button',
    partnerSlug,
  });
};

export const bwPayoutReceiptClickTracking = ({
  claimID,
  bookingID,
  payoutID,
  receiptType,
  isPaymentComplete,
}: IBWPayoutReceiptClickTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'click', {
    analyticsID: 'payout-receipt-button',
    claimID,
    bookingID,
    payoutID,
    receiptType,
    isPaymentComplete,
  });
};

export const bwCESScoreTracking = ({
  source,
  score,
  bookingID,
  claimReference,
  partnerName,
  partnerID,
  subsidiaryID,
  subsidiaryName,
  articleSlug,
  feedback,
  reviewed,
  policyTypeGroupName,
}: IBWCESScoreTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', source, {
    score,
    bookingReference: bookingID ?? null,
    ins: bookingID,
    claim_cla: claimReference,
    claimReference: claimReference ?? null,
    articleSlug: articleSlug ?? null,
    feedback: feedback ?? null,
    reviewed: reviewed ?? false,
    partnerSlug: partnerName,
    partnerName,
    partnerID,
    subsidiaryID,
    subsidiaryName,
    policyTypeGroupName,
  });
};

export const bwContactModalTracking = ({
  contactJourneyID,
  bookingReference,
  claimReference,
  partnerName,
  partnerID,
  subsidiaryID,
  subsidiaryName,
  contactPurpose,
  pageSlug,
  articleTitle,
  hasClosedContactModal,
  hasClickedReturnPreviousStep,
  hasClickedHaveMoreQuestionsButton,
  hasClickedViewAllArticlesButton,
  hasClickedSubmitEnquiryButton,
  hasSubmitEnquirySuccessfully,
  hasOpenedContactModal,
}: IBWContactModalTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;
  if (!contactJourneyID) {
    sessionStorage.setItem(CONTACT_JOURNEY_ID, new Date().toISOString());
  }

  window.bwtag('record', 'contact_journey', {
    contactJourneyID: contactJourneyID ?? getSessionStorage(CONTACT_JOURNEY_ID),
    bookingReference: valueOrNull(bookingReference),
    contactPurpose: valueOrNull(contactPurpose),
    pageSlug: valueOrNull(pageSlug),
    articleTitle: valueOrNull(articleTitle),
    hasClosedContactModal: hasClosedContactModal ?? false,
    hasClickedReturnPreviousStep: hasClickedReturnPreviousStep ?? false,
    hasClickedHaveMoreQuestionsButton: hasClickedHaveMoreQuestionsButton ?? false,
    hasClickedViewAllArticlesButton: hasClickedViewAllArticlesButton ?? false,
    hasClickedSubmitEnquiryButton: hasClickedSubmitEnquiryButton ?? false,
    hasSubmitEnquirySuccessfully: hasSubmitEnquirySuccessfully ?? false,
    hasOpenedContactModal: hasOpenedContactModal ?? false,
    ins: bookingReference,
    claimReference,
    claim_cla: claimReference,
    partnerName,
    partnerID,
    subsidiaryID,
    subsidiaryName,
  });
};

export const bwFNOLSectionTimeTracking = ({
  bookingID,
  partnerID,
  partnerName,
  subsidiaryID,
  subsidiaryName,
  policyTypeGroupName,
  timeSpent,
  claimJourneyID,
  claimTypeID,
  entryPoint,
  sectionName,
  claimTypeName,
  documentName,
  variant,
  isFirstSection,
}: IBWFNOLSectionTimeTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'fnol-section-time', {
    ins: bookingID,
    bookingID,
    partnerID,
    partnerName,
    subsidiaryID,
    subsidiaryName,
    timeSpent,
    policyTypeGroupName,
    claimJourneyID,
    claimTypeID,
    claimTypeName,
    entryPoint,
    sectionName,
    documentName,
    variant,
    isFirstSection,
  });
};

export const bwFNOLSubmittedPageTimeTracking = ({
  bookingID,
  partnerID,
  partnerName,
  subsidiaryID,
  subsidiaryName,
  policyTypeGroupName,
  timeSpent,
  claimTypeName,
  variant,
}: IBWFNOLSubmittedPageTimeTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'fnol-submitted-page-time', {
    ins: bookingID,
    bookingID,
    partnerID,
    partnerName,
    subsidiaryID,
    subsidiaryName,
    timeSpent,
    policyTypeGroupName,
    claimTypeName,
    variant,
  });
};

export const bwHelpArticleTimeTracking = ({ timeSpent, slug }: { timeSpent: number; slug: string }) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'help-article-time', {
    slug,
    timeSpent,
  });
};

export const bwLinkClickTracking = ({
  href,
  linkText,
  pagePath,
}: {
  href: string;
  linkText: string;
  pagePath: string;
}) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'link_click', {
    href,
    linkText,
    pagePath,
  });
};

export const bwQuickActionsTracking = ({
  bookingID,
  partnerID,
  partnerName,
  subsidiaryName,
  subsidiaryID,
  quickActionKey,
  quickActionLocation,
}: IBWQuickActionsTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'quick-actions', {
    ins: bookingID,
    bookingID,
    partnerID,
    partnerName,
    subsidiaryName,
    subsidiaryID,
    quickActionKey,
    quickActionLocation,
  });
};

export const bwOCREventTracking = (args: IBWOCREventArgs): void => {
  if (window.isHeadless || !window.bwtag) return;
  const claimJourneyID = args.claimJourneyID ?? getClaimJourneyID(args.bookingID) ?? '';
  window.bwtag('record', 'ocr_event', { ...args, claimJourneyID });
};

export const bwFileUploadEventTracking = (args: IBWFileUploadEventArgs): void => {
  if (window.isHeadless || !window.bwtag) return;
  const claimJourneyID = args.claimJourneyID ?? getClaimJourneyID(args.bookingID) ?? '';
  window.bwtag('record', 'file_upload_event', { ...args, claimJourneyID });
};

export const bwClaimStartedTracking = (args: IBWClaimStartedEventArgs): void => {
  if (window.isHeadless || !window.bwtag) return;
  const claimJourneyID = args.claimJourneyID ?? getClaimJourneyID(args.bookingID) ?? '';
  window.bwtag('record', 'claim_started', { ...args, claimJourneyID });
};

export const bwClaimSubmittedTracking = (args: IBWClaimSubmittedEventArgs): void => {
  if (window.isHeadless || !window.bwtag) return;
  const claimJourneyID = args.claimJourneyID ?? getClaimJourneyID(args.bookingID) ?? '';
  window.bwtag('record', 'claim_submitted', { ...args, claimJourneyID });
};

export const bwSelfServiceTracking = ({
  bookingID,
  partnerID,
  partnerName,
  subsidiaryName,
  subsidiaryID,
  loginFlowEntryPoint,
  loginFlowCompleted,
  claimFlowLoginless,
  claimFlowAgentAssisted,
  entryPoint,
  claimFlowClaimsLandingCheckpoint,
  claimFlowBookingCardCheckpoint,
  claimFlowQuickActionsCheckpoint,
  claimFlowClaimSelectorCheckpoint,
  claimFlowDraftResumedCheckpoint,
  claimFlowCompleted,
  modifyFlowEntryPoint,
  modifyFlowButtonCheckpoint,
  modifyFlowCompleted,
  cancelFlowEntryPoint,
  cancelFlowButtonCheckpoint,
  cancelFlowCompleted,
  isEligibleForCancellation,
  isEligibleForCancellationOnXcover,
  payoutFlowEntryPoint,
  payoutFlowGetPaidButtonCheckpoint,
  payoutFlowCompleted,
}: IBWSelfServiceTrackingArgs) => {
  if (window.isHeadless || !window.bwtag) return;

  window.bwtag('record', 'self-service', {
    ins: bookingID,
    bookingID,
    partnerID,
    partnerName,
    subsidiaryName,
    subsidiaryID,
    loginFlowEntryPoint,
    loginFlowCompleted,
    claimFlowLoginless,
    claimFlowAgentAssisted,
    entryPoint,
    claimFlowClaimsLandingCheckpoint,
    claimFlowBookingCardCheckpoint,
    claimFlowQuickActionsCheckpoint,
    claimFlowClaimSelectorCheckpoint,
    claimFlowDraftResumedCheckpoint,
    claimFlowCompleted,
    modifyFlowEntryPoint,
    modifyFlowButtonCheckpoint,
    modifyFlowCompleted,
    cancelFlowEntryPoint,
    cancelFlowButtonCheckpoint,
    cancelFlowCompleted,
    isEligibleForCancellation,
    isEligibleForCancellationOnXcover,
    payoutFlowEntryPoint,
    payoutFlowGetPaidButtonCheckpoint,
    payoutFlowCompleted,
  });
};
